/**
 * AI functions: models on Alibaba Cloud Model Studio (Singapore), one API key, set in the admin portal
 * 「AI 設定」. Besides Qwen, the Singapore region also serves DeepSeek, GLM and Kimi on the same endpoint
 * and key, so each AI function can use its own model and the data stays in that region. Every call is
 * logged for 「AI 用量」. Each function is an add-on module the platform assigns brand by brand.
 */
import { z } from 'zod';
import { AI_COPY_TONES } from './ai-content.ts';
import type { ModuleKey } from './growth.ts';

export type AiProvider = 'off' | 'test' | 'qwen';

/**
 * Text models offered in the admin portal, with Model Studio's Singapore list prices (US$ per million
 * tokens, 31/10/2026; DeepSeek at its busy-hour price). Any other model ID can be typed in as well.
 */
export const AI_TEXT_MODELS = [
  { id: 'qwen3.7-flash', label: 'Qwen 3.7 Flash（最平、最快）', inputPerM: 0.03, outputPerM: 0.13 },
  { id: 'qwen3.7-plus', label: 'Qwen 3.7 Plus（質素與價錢平衡）', inputPerM: 0.4, outputPerM: 1.6 },
  { id: 'qwen3.8-max', label: 'Qwen 3.8 Max（最強 Qwen）', inputPerM: 2, outputPerM: 6 },
  { id: 'deepseek-v4.1-flash', label: 'DeepSeek V4.1 Flash（中文自然，夜間半價）', inputPerM: 0.3, outputPerM: 1.2 },
  { id: 'deepseek-v4-pro', label: 'DeepSeek V4 Pro（推理強）', inputPerM: 2.4, outputPerM: 4.8 },
  { id: 'glm-5.3', label: 'GLM 5.3（結構化輸出穩定）', inputPerM: 1.4, outputPerM: 4.4 },
  { id: 'kimi-k3', label: 'Kimi K3（長文，最貴）', inputPerM: 3, outputPerM: 15 },
  { id: 'qwen-plus', label: 'Qwen Plus（舊版名稱）', inputPerM: 0.4, outputPerM: 1.2 },
  { id: 'qwen-flash', label: 'Qwen Flash（舊版名稱）', inputPerM: 0.05, outputPerM: 0.4 },
  { id: 'qwen-max', label: 'Qwen Max（舊版名稱）', inputPerM: 1.6, outputPerM: 6.4 },
] as const;

/** US$ per million tokens of a model in the list; null for a model typed in by hand. */
export function textModelPrice(model: string): { inputPerM: number; outputPerM: number } | null {
  return AI_TEXT_MODELS.find((m) => m.id === model) ?? null;
}

/**
 * The AI functions that write text, each with its add-on module and the model suggested for it. Numbers
 * (forecasts, member value, campaign results) are always computed by the platform; the model only explains them.
 */
export const AI_TEXT_FUNCTIONS = ['copy', 'translate', 'prompt', 'campaignPlan', 'segmentNl', 'campaignReview', 'memberValue', 'forecast', 'weeklyReport', 'onboarding'] as const;
export type AiTextFunction = (typeof AI_TEXT_FUNCTIONS)[number];

export const AI_FUNCTION_INFO: Record<AiTextFunction, { label: string; module: ModuleKey; recommended: string; why: string }> = {
  copy: { label: 'AI 文案助手', module: 'aiCopy', recommended: 'qwen3.7-plus', why: '中文推廣文案自然，價錢低；想更口語可試 DeepSeek V4.1 Flash' },
  translate: { label: 'AI 三語翻譯', module: 'aiTranslate', recommended: 'qwen3.7-flash', why: '翻譯不需深度推理，最平的模型已足夠' },
  prompt: { label: '優化圖片描述', module: 'aiImages', recommended: 'qwen3.7-flash', why: '把短構思擴寫成畫面描述，簡單工作' },
  campaignPlan: { label: 'AI 推廣活動企劃', module: 'aiCampaignPlanner', recommended: 'qwen3.8-max', why: '要推理及輸出完整方案；用量少，成本仍低' },
  segmentNl: { label: 'AI 一句話分群', module: 'aiSegments', recommended: 'qwen3.7-plus', why: '把說話轉成分群條件，要穩定的結構化輸出' },
  campaignReview: { label: 'AI 推廣成效分析', module: 'aiCampaignReview', recommended: 'qwen3.7-plus', why: '數字由系統計算，模型只寫結論' },
  memberValue: { label: 'AI 會員價值預測', module: 'aiMemberValue', recommended: 'qwen3.7-flash', why: '預測由系統計算，模型只寫簡短說明' },
  forecast: { label: 'AI 客流及銷售預測', module: 'aiForecast', recommended: 'qwen3.7-flash', why: '預測由系統計算，模型只寫簡短說明' },
  weeklyReport: { label: 'AI 每週營運周報', module: 'aiWeeklyReport', recommended: 'qwen3.7-plus', why: '總結一週數字並提出建議，質素與價錢平衡' },
  onboarding: { label: 'AI 開店助手', module: 'aiOnboarding', recommended: 'qwen3.8-max', why: '要讀長網頁並整理成品牌資料；每個品牌只用數次' },
};

export const AI_IMAGE_MODELS = [
  { id: 'qwen-image-2.1-turbo', label: 'Qwen Image 2.1 Turbo（建議：快、平）', perImage: 0.016 },
  { id: 'qwen-image-3.0', label: 'Qwen Image 3.0（質素較好）', perImage: 0.03 },
  { id: 'qwen-image-2.1-pro', label: 'Qwen Image 2.1 Pro', perImage: 0.04 },
  { id: 'qwen-image-3.0-pro', label: 'Qwen Image 3.0 Pro（最佳質素，每分鐘 5 張）', perImage: 0.04 },
] as const;

export const DEFAULT_AI_BASE_URL = 'https://dashscope-intl.aliyuncs.com';

/** Shapes an image can be made in; each upload box asks for the closest one. */
export const AI_IMAGE_SHAPES = ['1:1', '4:3', '3:4', '4:5', '16:9', '9:16'] as const;
export type AiImageShape = (typeof AI_IMAGE_SHAPES)[number];

const modelId = z.string().trim().regex(/^[a-z0-9][a-z0-9._-]{1,63}$/);
const price = z.number().min(0).max(1000);

export const aiConfigSchema = z.object({
  provider: z.enum(['off', 'test', 'qwen']),
  /** Model per AI function; a function not listed uses textModel. Absent on save = keep the stored ones. */
  functionModels: z.record(z.enum(AI_TEXT_FUNCTIONS), modelId).optional(),
  /** Write-only; empty or absent keeps the stored key. */
  apiKey: z.string().trim().max(200).optional(),
  /** Model Studio endpoint; Singapore by default, or the workspace endpoint shown in the console. */
  baseUrl: z.string().trim().url().regex(/^https:\/\/[a-z0-9.-]+\.aliyuncs\.com\/?$/),
  textModel: modelId,
  imageModel: modelId,
  /** US$ used for the cost estimates in 「AI 用量」. */
  textInputPerM: price,
  textOutputPerM: price,
  imagePrice: price,
  /** Platform-wide safety cap on images per Hong Kong day. */
  dailyImageCap: z.number().int().min(1).max(100_000),
  /** Images a month for a brand on the add-on without a limit set (no plan, or the plan says 不限). */
  defaultMonthlyImages: z.number().int().min(0).max(100_000),
});
export type AiConfigInput = z.infer<typeof aiConfigSchema>;

export interface AiConfigMasked extends Omit<AiConfigInput, 'apiKey'> {
  /** Last 4 characters of the stored key, or null when none is stored. */
  apiKeyEnd: string | null;
  imagesToday: number;
}

export const aiTestSchema = z.object({
  kind: z.enum(['text', 'image']),
  /** Text only: try this model instead of the default one (e.g. before giving it to a function). */
  model: modelId.optional(),
  /** Added by the admin API: who pressed 測試, for the log. */
  actor: z.string().max(200).optional(),
});

export interface AiTestResult {
  ok: boolean;
  /** The model's reply (text) or a link to the test image (image, valid 24 hours). */
  output: string | null;
  error: string | null;
  ms: number;
}

/** Brand portal: what the AI buttons can do now. */
export interface PortalAiStatus {
  /** The platform has AI switched on and the brand has the add-on. */
  images: boolean;
  /** 優化描述 (text model) is available. */
  text: boolean;
  /** AI text functions this brand can use now (platform AI on + the function's add-on module). */
  functions: AiTextFunction[];
  imagesThisMonth: number;
  /** null = no limit. */
  imagesLimit: number | null;
}

export const aiImageSchema = z.object({
  prompt: z.string().trim().min(2).max(800),
  shape: z.enum(AI_IMAGE_SHAPES),
  /** Where the picture is for (e.g. 「品牌標誌」), logged with the call. */
  purpose: z.string().trim().max(60).optional(),
});
export type AiImageInput = z.infer<typeof aiImageSchema>;

export interface AiImageResult {
  key: string;
  url: string;
  imagesThisMonth: number;
  imagesLimit: number | null;
}

export const aiPromptSchema = z.object({
  idea: z.string().trim().min(2).max(400),
  purpose: z.string().trim().max(60).optional(),
  /** The picture's mood follows this copy tone (a 推廣活動's). */
  tone: z.enum(AI_COPY_TONES).optional(),
});

export interface AiPromptResult {
  prompt: string;
}

/** What an ai_log row was for: an image, the admin test, or one of the text functions. */
export type AiFeature = 'image' | 'test' | AiTextFunction;

export interface AiLogRow {
  id: string;
  createdAt: string;
  brandId: string | null;
  brandName: string | null;
  actor: string | null;
  feature: AiFeature;
  model: string;
  /** The request (prompt) as sent, trimmed. */
  input: string;
  inputTokens: number;
  outputTokens: number;
  images: number;
  costUsd: number;
  status: 'ok' | 'failed' | 'test';
  error: string | null;
  ms: number;
}

/** 「AI 用量」 for a date range (Hong Kong days, inclusive). */
export interface AiUsageReport {
  from: string;
  to: string;
  totals: { calls: number; images: number; inputTokens: number; outputTokens: number; costUsd: number; failed: number };
  /** One entry per day in the range. */
  daily: Array<{ date: string; images: number; textCalls: number; costUsd: number }>;
  byBrand: Array<{ brandId: string | null; brandName: string | null; images: number; textCalls: number; costUsd: number; imagesThisMonth: number; imagesLimit: number | null }>;
  byModel: Array<{ model: string; feature: AiFeature; calls: number; images: number; inputTokens: number; outputTokens: number; costUsd: number }>;
}
